/**
 * Abbreviate a number to a shorter format with unit suffixes (K, M, B, T).
 *
 * @param value - The number to abbreviate
 * @param options - Formatting options
 * @param options.units - Custom unit suffixes (default: ['', 'K', 'M', 'B', 'T'])
 * @param options.divisor - Divisor for each unit level (default: 1000)
 * @param options.decimalPlaces - Number of decimal places (default: 1)
 * @param options.hideDecimalsForIntegers - Hide decimals for whole numbers (default: true)
 * @param options.hideDecimalsAbove - Hide decimals when value >= this number (default: 10)
 * @param options.locale - Locale for number formatting (default: undefined, uses system)
 *
 * @returns Abbreviated number string
 *
 * @example
 * abbreviateNumber(999) // '999'
 * abbreviateNumber(1000) // '1K'
 * abbreviateNumber(1500) // '1.5K'
 * abbreviateNumber(15000) // '15K'
 * abbreviateNumber(1500000) // '1.5M'
 * abbreviateNumber(1500, { units: ['', 'k', 'm', 'b', 't'] }) // '1.5k'
 * abbreviateNumber(1500, { divisor: 1024 }) // '1.5K' (for binary units)
 * abbreviateNumber(1500, { decimalPlaces: 2 }) // '1.50K'
 */
export function abbreviateNumber(
  value: number,
  options: {
    units?: string[]
    divisor?: number
    decimalPlaces?: number
    hideDecimalsForIntegers?: boolean
    hideDecimalsAbove?: number
    locale?: string
  } = {},
): string {
  const {
    units = ['', 'K', 'M', 'B', 'T', 'P', 'E'],
    divisor = 1000,
    decimalPlaces = 1,
    hideDecimalsForIntegers = true,
    hideDecimalsAbove = 10,
    locale,
  } = options

  if (value < 0) {
    return '-' + abbreviateNumber(Math.abs(value), options)
  }

  if (value < divisor) {
    return locale ? value.toLocaleString(locale) : value.toString()
  }

  const unitIndex = Math.min(Math.floor(Math.log(value) / Math.log(divisor)), units.length - 1)

  const scaledValue = value / Math.pow(divisor, unitIndex)

  // Determine if we should show decimals
  const isInteger = scaledValue % 1 === 0
  const isAboveThreshold = scaledValue >= hideDecimalsAbove

  let formattedValue: string
  if ((hideDecimalsForIntegers && isInteger) || isAboveThreshold) {
    formattedValue = Math.floor(scaledValue).toString()
  } else {
    formattedValue = scaledValue.toFixed(decimalPlaces)
    // Remove trailing zeros and decimal point if not needed
    formattedValue = formattedValue.replace(/\.?0+$/, '')
  }

  return formattedValue + units[unitIndex]
}

/**
 * Format views, likes, or similar social metrics.
 *
 * @example
 * formatViewsAndLikes(999) // '999'
 * formatViewsAndLikes(1500) // '1.5K'
 * formatViewsAndLikes(15000) // '15K'
 */
export function formatViewsAndLikes(number: number): string {
  return abbreviateNumber(number)
}

/**
 * Format file sizes with binary units (KiB, MiB, GiB, etc.).
 *
 * @example
 * formatFileSize(1024) // '1KiB'
 * formatFileSize(1536) // '1.5KiB'
 * formatFileSize(1048576) // '1MiB'
 */
export function formatFileSize(bytes: number, useDecimal = false): string {
  const divisor = useDecimal ? 1000 : 1024
  const units = useDecimal ? ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] : ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB']

  return abbreviateNumber(bytes, {
    units,
    divisor,
    decimalPlaces: 2,
  })
}

/**
 * Format currency in abbreviated form.
 *
 * @example
 * formatAbbreviatedCurrency(1500, '$') // '$1.5K'
 * formatAbbreviatedCurrency(1500000, 'Rp') // 'Rp1.5M'
 */
export function formatAbbreviatedCurrency(value: number, currencySymbol: string): string {
  return currencySymbol + abbreviateNumber(value)
}
